<script lang="ts">
    import { navigate } from '../lib/router.svelte';
  import AdminShell from './AdminShell.svelte';
  import PageContainer from '../lib/ui/PageContainer.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import StatCard from '../lib/ui/StatCard.svelte';
  import Avatar from '@ui/Avatar.svelte';
  import MemberHover from '@ui/MemberHover.svelte';
  import ChartTooltip from '@ui/ChartTooltip.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';
  import {
    redirectIfAwaitingOnboarding,
    getToken,
    handle401,
    timeAgo,
    type AdminUser,
    type AiInsights,
  } from '../lib/admin';
  import { fetchAiInsights } from '@api';
  import { community, withCommunity, onCommunityChange } from '../lib/community.svelte';
  import { asAdminUser, cachedBootstrap, ensureBootstrap } from '../lib/session';
  import { fetchConversation, type ConversationItem } from '@api';

  let me = $state<AdminUser | null>(null);
  let data = $state<AiInsights | null>(null);
  let loading = $state(true);
  let dataLoading = $state(false);
  let error = $state<string | null>(null);

  // Conversation ledger: which rows are expanded + their lazily-fetched transcripts.
  // The transcript is on v1 (`GET /v1/communities/{id}/ai/conversations/{id}` — one
  // `ConversationItem` schema for the ledger row, the merged stream and this).
  //
  // THE AGGREGATE ABOVE IT IS NOT, and that is a reported backend gap rather than a
  // choice. Re-measured 2026-07-29 against the running backend on skool 4 — the only
  // community in the DB with AI traffic (105 conversations, 227 questions, 21 askers)
  // — the gap is now ONE missing block rather than two:
  //
  //   * `ai_headline` / `ai_weekly` / `ai_sources` / `ai_feedback` come back EQUAL to
  //     this response's `headline` / `weekly` / `top_sources` / `feedback_comments`.
  //   * `data.conversations` equals `GET …/ai/conversations?limit=30` — same 30 ids in
  //     the same order; the v1 row only adds `asker_key`/`asker_name`/`asker_handle`.
  //   * `top_askers` is exactly reconstructible from `GET …/ai/askers`: sort by
  //     `questions` desc, take 12, read `member?.name ?? name`. Verified identical on
  //     all twelve rows. But that route is keyset-paged by LAST ACTIVITY, so the
  //     reconstruction is only right while every asker fits in one page, and it ships
  //     ~20 KB of per-asker citation lists this page never renders.
  //   * `ungrounded_questions` — asks whose answer cited nothing — has NO v1 source.
  //     Not a block, not a route, and not derivable client-side without pulling every
  //     conversation's messages, which is the over-fetch v1 exists to remove.
  //
  // So splitting the page TODAY costs 3 requests / ~40 KB against this one's 28.7 KB
  // and still cannot render one of its five sections. It stays on
  // `/admin/analytics/ai` until an `ai_ungrounded` block exists (plus, to remove the
  // scale cliff, an askers-BY-VOLUME block; `…/ai/askers` is by recency).
  //
  // One more reason not to half-move it: `last_updated` here is the newest
  // CONVERSATION's timestamp, which is what the chip below reads. The v1 insights
  // envelope's `last_updated` is the member-stats rebuild time, so it would light
  // that chip on a community that has never had a conversation.
  let expanded = $state<Set<number>>(new Set());
  let transcripts = $state<Map<number, ConversationItem | 'loading'>>(new Map());

  let nowMs = $state(Date.now());
  $effect(() => {
    const id = setInterval(() => (nowMs = Date.now()), 30_000);
    return () => clearInterval(id);
  });

  /** The community the last fetch was issued for — claimed BEFORE the await, so the
   *  effect below can tell "already loading this one" from "the viewer switched". */
  let lastId: number | null = null;
  /** `init` has settled and owns the first fetch. On a FIRST visit (no id in
   *  localStorage) `community.loaded` flips while `withCommunity` is still deciding
   *  which id to use; without this gate the effect would duplicate that fetch. */
  let ready = $state(false);

  /** Throws on failure: `withCommunity` needs to see a scope error to retry against
   *  the bootstrap's resolved id. */
  async function fetchAll(id: number): Promise<void> {
    lastId = id;
    expanded = new Set();
    transcripts = new Map();
    data = await fetchAiInsights(id);
  }

  async function init(): Promise<void> {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    loading = true;
    error = null;
    try {
      // The bootstrap and this page's own fetch go out in the SAME round trip,
      // against the id localStorage already held (doc 04 §4.1) — the preamble no
      // longer gates the data.
      const [boot] = await Promise.all([ensureBootstrap(), withCommunity(fetchAll)]);
      me = asAdminUser(boot);
      if (redirectIfAwaitingOnboarding(me, navigate)) return;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      const boot = cachedBootstrap();
      if (boot) {
        me = asAdminUser(boot);
        if (redirectIfAwaitingOnboarding(me, navigate)) return;
      }
      error = err instanceof Error ? err.message : 'Failed to load AI insights.';
    } finally {
      loading = false;
      ready = true;
    }
  }

  // Reload when the shared community selection changes — and only then.
  onCommunityChange((id) => void load(id), { gate: () => ready });

  async function load(id: number): Promise<void> {
    dataLoading = true;
    error = null;
    try {
      await fetchAll(id);
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load AI insights.';
      data = null;
    } finally {
      dataLoading = false;
    }
  }

  async function toggleConv(convId: number): Promise<void> {
    if (expanded.has(convId)) {
      const n = new Set(expanded);
      n.delete(convId);
      expanded = n;
      return;
    }
    const n = new Set(expanded);
    n.add(convId);
    expanded = n;
    const cid = community.loaded ? community.selectedId : lastId;
    if (cid == null || transcripts.has(convId)) return;
    const m = new Map(transcripts);
    m.set(convId, 'loading');
    transcripts = m;
    try {
      const conv = await fetchConversation(cid, convId);
      const m2 = new Map(transcripts);
      m2.set(convId, conv);
      transcripts = m2;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      const m2 = new Map(transcripts);
      m2.delete(convId);
      transcripts = m2;
      const e = new Set(expanded);
      e.delete(convId);
      expanded = e;
    }
  }

  const h = $derived(data?.headline);
  const pct = (v: number | null | undefined): string => (v == null ? '—' : `${Math.round(v * 100)}%`);
  const weekMax = $derived(Math.max(1, ...(data?.weekly ?? []).map((w) => w.questions)));
  const weekLabel = (w: string): string =>
    new Date(w + 'T00:00:00Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  // Deep-links into the conversations page, pre-filtered by where the click came from.
  const askerConvLink = (a: { external_id: string | null }): string =>
    a.external_id ? `/admin/conversations?user=${encodeURIComponent(a.external_id)}` : '/admin/conversations';
  const sourceConvLink = (pageTitle: string): string =>
    `/admin/conversations?source=${encodeURIComponent(pageTitle)}`;

  // Rich hovercard for the weekly bars (questions · conversations · unique askers).
  type Week = { week: string; conversations: number; questions: number; askers?: number };
  let weekHover = $state<{ w: Week; x: number; y: number } | null>(null);
  function weekEnter(e: MouseEvent, w: Week): void {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    weekHover = { w, x: r.left + r.width / 2, y: r.top };
  }

  void init();
</script>

<AdminShell {me}>
  {#if loading}
    <StateBlock state="loading" />
  {:else if error && !data}
    <StateBlock state="error" message={error} onretry={init} />
  {:else}
    <PageContainer>
      <!-- Three names for one page: the eyebrow said "Sideguide AI · Usage", the title
           said "AI Assistant", the sidebar says "Usage". The sidebar wins.
           `updatedLabel` because this stamp is the newest CONVERSATION, not a pipeline
           run — the one page here where "updated" means something more specific. -->
      <PageHeader
        section="Sideguide AI"
        title="Usage"
        updated={data?.last_updated}
        updatedLabel="Last conversation"
      />

      {#if dataLoading}
        <StateBlock state="loading" />
      {:else if data && h}
        {#if h.conversations === 0}
          <StateBlock state="empty" message="No conversations yet" hint="Once members chat with Sideguide AI here, their usage shows up on this page." />
        {:else}
          <!-- 1 · Headline KPIs -->
          <div class="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <StatCard label="Conversations" value={h.conversations.toLocaleString()} sub="chat threads" />
            <StatCard label="Questions asked" value={h.questions.toLocaleString()} sub="member turns" />
            <StatCard label="People asking" value={h.askers.toLocaleString()} sub="unique members" />
            <StatCard
              label="Grounded answers"
              value={pct(h.sourced_rate)}
              tone={h.sourced_rate != null && h.sourced_rate >= 0.7 ? 'success' : 'default'}
              sub={h.answers ? `${h.sourced.toLocaleString()} of ${h.answers.toLocaleString()} cited content` : 'cited your content'}
            />
            <StatCard
              label="Answer rating"
              value={h.rated > 0 ? pct(h.positive_rate) : '—'}
              tone={h.rated > 0 && (h.positive_rate ?? 0) >= 0.7 ? 'success' : 'default'}
              sub={h.rated > 0 ? `${h.feedback_up} 👍 ${h.feedback_down} 👎 · ${h.rated} rated` : 'no ratings yet'}
            />
          </div>
          <p class="mt-3 text-xs text-base-content/75">
            {h.returning_askers} of {h.askers} askers came back in a later week · {h.avg_tool_rounds?.toFixed(1) ?? '—'} avg search rounds per answer.
          </p>

          <!-- 2 · Weekly volume -->
          <section class="mt-6 rounded-box border hair bg-base-100 p-5">
            <div class="eyebrow mb-4">Questions per week</div>
            {#if data.weekly.length === 0}
              <p class="text-sm text-base-content/62">Not enough history yet.</p>
            {:else}
              <div class="flex h-32 items-end gap-2">
                {#each data.weekly as w (w.week)}
                  <div class="group flex min-w-0 flex-1 flex-col items-center gap-1.5">
                    <span class="font-mono text-[0.65rem] tabular-nums text-base-content/68">{w.questions}</span>
                    <div
                      class="w-full cursor-default rounded-t bg-primary/70 transition-colors group-hover:bg-primary"
                      style="height:{Math.max(3, (w.questions / weekMax) * 88)}px"
                      role="presentation"
                      onmouseenter={(e) => weekEnter(e, w)}
                      onmouseleave={() => (weekHover = null)}
                    ></div>
                    <span class="truncate font-mono text-[0.6rem] text-base-content/62">{weekLabel(w.week)}</span>
                  </div>
                {/each}
              </div>
            {/if}
          </section>

          {#if weekHover}
            {@const wh = weekHover}
            <ChartTooltip x={wh.x} y={wh.y}>
              <div class="font-mono text-[0.7rem] font-semibold">Week of {weekLabel(wh.w.week)}</div>
              <div class="mt-1 space-y-0.5 text-xs text-base-content/75">
                <div><span class="font-semibold text-base-content">{wh.w.questions}</span> questions</div>
                <div><span class="font-semibold text-base-content">{wh.w.conversations}</span> conversations</div>
                {#if wh.w.askers != null}<div><span class="font-semibold text-base-content">{wh.w.askers}</span> {wh.w.askers === 1 ? 'member' : 'members'} asking</div>{/if}
              </div>
            </ChartTooltip>
          {/if}

          <!-- 3 · Top askers · most-referenced content -->
          <div class="mt-6 grid gap-6 lg:grid-cols-2">
            <section class="rounded-box border hair bg-base-100 p-5">
              <div class="eyebrow mb-1">Top askers</div>
              <p class="mb-3 text-xs text-base-content/75">Members leaning on the assistant most — open their AI conversations.</p>
              <ul class="space-y-0.5">
                {#each data.top_askers as a, i (a.external_id ?? a.name + i)}
                  <li class="flex items-center gap-3 rounded-field px-1.5 py-1.5">
                    <span class="w-4 shrink-0 text-right font-mono text-xs text-base-content/58">{i + 1}</span>
                    {#if a.member_id != null}
                      <MemberHover memberId={a.member_id} class="shrink-0">
                        <Avatar src={a.picture_profile} name={a.name} size={28} memberId={a.member_id} />
                      </MemberHover>
                    {:else}
                      <span class="shrink-0"><Avatar src={a.picture_profile} name={a.name} size={28} /></span>
                    {/if}
                    <button class="redact min-w-0 flex-1 truncate text-left text-sm font-medium transition-colors hover:text-primary" onclick={() => navigate(askerConvLink(a))} title="Read {a.name}'s AI conversations">{a.name}</button>
                    {#if a.returning}<span class="shrink-0 rounded-full bg-success/10 px-1.5 py-0.5 text-[0.6rem] font-medium text-success">returning</span>{/if}
                    <span class="shrink-0 font-mono text-[0.7rem] tabular-nums text-base-content/75">{a.questions}q · {a.conversations}c</span>
                  </li>
                {/each}
              </ul>
            </section>

            <section class="rounded-box border hair bg-base-100 p-5">
              <div class="eyebrow mb-1">Most-referenced content</div>
              <p class="mb-3 text-xs text-base-content/75">Lessons the assistant answers from most — click to see the conversations that cite each.</p>
              {#if data.top_sources.length === 0}
                <p class="text-sm text-base-content/62">No sourced answers yet.</p>
              {:else}
                <ul class="space-y-0.5">
                  {#each data.top_sources as s (s.page_title)}
                    <li>
                      <!-- privacy-ok: the href and the click handler carry the title into
                           a URL, never onto the screen; the painted copy is marked below -->
                      <a
                        href={sourceConvLink(s.page_title)}
                        onclick={(e) => { e.preventDefault(); navigate(sourceConvLink(s.page_title)); }}
                        class="group -mx-1.5 flex items-start gap-2.5 rounded-field px-1.5 py-1 transition-colors hover:bg-base-200/50"
                        title="See conversations that cite “{s.page_title}”"
                      >
                        <span class="mt-px shrink-0 rounded bg-secondary/10 px-1.5 py-0.5 font-mono text-[0.7rem] font-medium tabular-nums text-secondary">{s.citations}</span>
                        <div class="min-w-0">
                          <div class="redact truncate text-sm transition-colors group-hover:text-primary">{s.page_title}</div>
                          {#if s.course_title}<div class="redact truncate font-mono text-[0.7rem] text-base-content/75">{s.course_title}</div>{/if}
                        </div>
                      </a>
                    </li>
                  {/each}
                </ul>
              {/if}
            </section>
          </div>

          <!-- 4 · Conversations ledger (centerpiece) -->
          <section class="mt-6 rounded-box border hair bg-base-100 p-5">
            <div class="eyebrow mb-1">Conversations</div>
            <p class="mb-3 text-xs text-base-content/75">What members are actually doing with the assistant — newest first. Click to read the full thread.</p>
            <div class="divide-y hair overflow-hidden rounded-field border hair bg-base-100/50">
              {#each data.conversations as c (c.id)}
                {@const open = expanded.has(c.id)}
                {@const t = transcripts.get(c.id)}
                <div>
                  <button
                    class="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-base-200/50"
                    onclick={() => toggleConv(c.id)}
                    aria-expanded={open}
                  >
                    {#if c.asker.member_id != null}
                      <MemberHover memberId={c.asker.member_id} class="shrink-0">
                        <Avatar src={c.asker.picture_profile} name={c.asker.name} size={26} memberId={c.asker.member_id} />
                      </MemberHover>
                    {:else}
                      <span class="shrink-0"><Avatar src={c.asker.picture_profile} name={c.asker.name} size={26} /></span>
                    {/if}
                    <div class="min-w-0 flex-1">
                      <div class="redact truncate text-sm text-base-content/90">{c.title || '(no text)'}</div>
                      <div class="mt-0.5 flex items-center gap-1.5 font-mono text-[0.65rem] text-base-content/62">
                        <span class="redact truncate">{c.asker.name}</span>
                        <span>·</span>
                        <span>{c.turns} turn{c.turns === 1 ? '' : 's'}</span>
                        {#if c.last_at}<span>·</span><span>{timeAgo(c.last_at, nowMs)}</span>{/if}
                      </div>
                    </div>
                    <div class="flex shrink-0 items-center gap-1.5 text-xs">
                      {#if c.sourced}<span title="Cited your content">🔗</span>{/if}
                      {#if c.rating}<span title={c.rating === 'up' ? 'Rated helpful' : 'Rated unhelpful'}>{c.rating === 'up' ? '👍' : '👎'}</span>{/if}
                      <svg class="h-4 w-4 text-base-content/54 transition-transform {open ? 'rotate-90' : ''}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                    </div>
                  </button>
                  {#if open}
                    <div class="border-t hair bg-base-100 px-3 py-3">
                      {#if t === 'loading' || t === undefined}
                        <StateBlock state="loading" compact />
                      {:else}
                        <div class="space-y-3">
                          {#each t.messages ?? [] as m, mi (mi)}
                            <div class="flex flex-col gap-1 {m.role === 'user' ? 'items-start' : 'items-stretch'}">
                              <div class="redact eyebrow text-base-content/58">{m.role === 'user' ? c.asker.name : 'Sideguide AI'}</div>
                              <div class="whitespace-pre-wrap rounded-field px-3 py-2 text-sm leading-relaxed {m.role === 'user' ? 'bg-base-100 text-base-content/90' : 'bg-primary/[0.06] text-base-content/85'}">{m.content}</div>
                              {#if m.role === 'assistant' && m.sources.length > 0}
                                <div class="flex flex-wrap gap-1.5 pl-1">
                                  {#each m.sources as s, si (si)}
                                    <span class="rounded-full bg-secondary/10 px-2 py-0.5 font-mono text-[0.65rem] text-secondary">
                                      <span class="redact">{s.page_title ?? 'source'}{#if s.course_title} · {s.course_title}{/if}</span>
                                    </span>
                                  {/each}
                                </div>
                              {/if}
                            </div>
                          {/each}
                        </div>
                      {/if}
                    </div>
                  {/if}
                </div>
              {/each}
            </div>
          </section>

          <!-- 5 · Assistant blind spots + feedback comments -->
          <section class="mt-6 rounded-box border hair bg-base-100 p-5">
            <div class="eyebrow mb-1">Questions the assistant couldn't ground</div>
            <p class="mb-3 text-xs text-base-content/75">The assistant answered from general knowledge — likely gaps in your classroom content.</p>
            {#if data.ungrounded_questions.length === 0}
              <p class="text-sm text-base-content/62">Every answer cited your content. Nice.</p>
            {:else}
              <ul class="space-y-2">
                {#each data.ungrounded_questions as q (q.conversation_id + (q.created_at ?? '') + q.text.slice(0, 12))}
                  <li class="flex items-start gap-3 rounded-field bg-base-100/40 px-3 py-2">
                    <div class="min-w-0 flex-1">
                      <p class="line-clamp-2 text-sm text-base-content/90">{q.text}</p>
                      <div class="mt-0.5 font-mono text-[0.65rem] text-base-content/62">
                        {q.asker}{#if q.created_at} · {timeAgo(q.created_at, nowMs)}{/if}
                      </div>
                    </div>
                  </li>
                {/each}
              </ul>
            {/if}
          </section>

          {#if data.feedback_comments.length > 0}
            <section class="mt-6 rounded-box border hair bg-base-100 p-5">
              <div class="eyebrow mb-1">Answer feedback</div>
              <p class="mb-3 text-xs text-base-content/75">What members said when they rated an answer.</p>
              <ul class="space-y-2.5">
                {#each data.feedback_comments as f (f.created_at)}
                  <li class="rounded-field bg-base-100/50 px-3 py-2.5">
                    <div class="flex items-center gap-2">
                      <span class="shrink-0">{f.rating === 'up' ? '👍' : '👎'}</span>
                      {#if f.question}<span class="redact truncate text-xs text-base-content/72">Q: {f.question}</span>{/if}
                    </div>
                    <p class="mt-1 text-sm text-base-content/85">{f.comment}</p>
                  </li>
                {/each}
              </ul>
            </section>
          {/if}
        {/if}
      {/if}
    </PageContainer>
  {/if}
</AdminShell>
